import { HOME_LICENSES_STRINGS } from 'accounts/constants/strings/home-licenses.strings';
import { DateInputFinalFormAdapter } from 'components/DateInput/DateInput';
import { DropdownFinalFormAdapter } from 'components/Dropdown/Dropdown';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  maskCurrency,
  maskMMDDYYYY,
  unmaskMMDDYYYY,
  unmaskNumber,
} from 'components/Input/masks';
import { get } from 'es-toolkit/compat';
import { Col, Row } from 'forkedlibraries/react-bootstrap';
import {
  composeValidators,
  dateValidationMMDDYY,
  required,
} from 'utils/validation';

// The section of the DC Rental Housing Act that allowed each adjustment. The
// stored value is the `key`, which is the `DCRentAdjustment.basis` wire value; a
// comparable-unit number is only required when the basis is the comparable-vacancy
// provision (§213(a)(2)).
const RENT_BASIS_COMPARABLE_UNIT = 'ComparableUnitVacancy213a2';

const RENT_BASIS_OPTIONS = [
  { key: 'AnnualCpiW206b', value: '206(b) — Annual CPI-W increase' },
  { key: 'CapitalImprovement210', value: '210 — Capital improvement' },
  {
    key: 'ServicesFacilities211',
    value: '211 — Change in services/facilities',
  },
  { key: 'Hardship212', value: '212 — Hardship petition' },
  { key: 'Vacancy213a1', value: '213(a)(1) — Vacancy (10%)' },
  {
    key: RENT_BASIS_COMPARABLE_UNIT,
    value: '213(a)(2) — Comparable-unit vacancy (30% max)',
  },
  {
    key: 'SubstantialRehabilitation214',
    value: '214 — Substantial rehabilitation',
  },
  { key: 'VoluntaryAgreement215', value: '215 — Voluntary agreement' },
];

interface RentAdjustmentFieldsProps {
  name: string;
  // The full form values object, supplied by StandardFieldArray.
  values: Record<string, unknown>;
}

export function RentAdjustmentFields({
  name,
  values,
}: RentAdjustmentFieldsProps) {
  const isComparableUnit =
    get(values, `${name}.basis`) === RENT_BASIS_COMPARABLE_UNIT;

  return (
    <div>
      <Row>
        <Col xs={24} sm={12} md={6}>
          <Field
            name={`${name}.year`}
            component={InputFinalFormAdapter}
            type="number"
            placeholder={HOME_LICENSES_STRINGS['rent_history.year_placeholder']}
            validate={required}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Field
            name={`${name}.priorRent`}
            component={InputFinalFormAdapter}
            mask={maskCurrency}
            unmask={unmaskNumber}
            placeholder={
              HOME_LICENSES_STRINGS['rent_history.prior_rent_placeholder']
            }
            validate={required}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Field
            name={`${name}.newRent`}
            component={InputFinalFormAdapter}
            mask={maskCurrency}
            unmask={unmaskNumber}
            placeholder={
              HOME_LICENSES_STRINGS['rent_history.new_rent_placeholder']
            }
            validate={required}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Field
            name={`${name}.effectiveDate`}
            component={DateInputFinalFormAdapter}
            mask={maskMMDDYYYY}
            unmask={unmaskMMDDYYYY}
            placeholder={
              HOME_LICENSES_STRINGS['rent_history.effective_placeholder']
            }
            validate={composeValidators(required, dateValidationMMDDYY)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} md={12}>
          <Field
            name={`${name}.basis`}
            component={DropdownFinalFormAdapter}
            items={RENT_BASIS_OPTIONS}
            placeholder={
              HOME_LICENSES_STRINGS['rent_history.basis_placeholder']
            }
            validate={required}
          />
        </Col>
        {isComparableUnit && (
          <Col xs={24} md={12}>
            <Field
              name={`${name}.comparableUnit`}
              component={InputFinalFormAdapter}
              placeholder={
                HOME_LICENSES_STRINGS[
                  'rent_history.comparable_unit_placeholder'
                ]
              }
              validate={required}
            />
          </Col>
        )}
      </Row>
      {isComparableUnit && (
        <p className="mt-xs text-caption text-gray">
          {HOME_LICENSES_STRINGS['rent_history.comparable_unit_helper']}
        </p>
      )}
    </div>
  );
}
